import { useEditController } from "ra-core";
import { createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { HintEntry, TableColumnRenameHintsDto } from "@/gen/api/encrypt";
import { useDbEscStatusContext } from "@/pages/databases/DatabaseEncryptionSettingChange";


const ESCRenameHintContext = createContext<ESCRenameHintControllerProps>({} as any);

export const useESCRenameHintContext = () => useContext<ESCRenameHintControllerProps>(ESCRenameHintContext)


interface State {
  toAdd: Record<string, HintEntry>,
  toRemove: Set<string>,
  existingHints: Record<string, HintEntry>,
  effectiveHints: Record<string, { renamedOnTable:boolean, hints: HintEntry[] }>,
}

export interface ESCRenameHintControllerProps {
  effectiveHints: Record<string, { renamedOnTable:boolean, hints: HintEntry[] }>,
  addHint: (from: TableField, to: TableField) => void,
  removeHint: (hint: HintEntry) => void,
  resetEdits: () => void,
  saveHints: (x: any) => void,
  refetchHints: () => void,
  saving: boolean,
  hasChanges: boolean,
}

export const ESCRenameHintContextProvider : FC = ({
  children,
}) => {
  const { data: dbStatus } = useDbEscStatusContext()
  const editCtrls = useEditController({ 
    resource: dbStatus ? 'table-rename-hint' : 'noop', 
    basePath: '',
    id: dbStatus?.databaseId || -1,
    mutationMode: 'pessimistic',
  })
  const renameHints : TableColumnRenameHintsDto|undefined = editCtrls.record as any

  const [states, setStates] = useState<State>({
    toAdd: {},
    toRemove: new Set<string>(),
    existingHints: groupExistingHints(renameHints),
    effectiveHints: {},
  })

  useEffect(() => {
    setStates(v => {
      const v1 = { ...v }
      v1.existingHints = groupExistingHints(renameHints)
      v1.effectiveHints = mergeToGroupedHints(v1)
      return v1
    })
  }, [renameHints])

  const modifiers = useHintsModifier(setStates)

  const hasChanges = states.toRemove.size > 0 || Object.keys(states.toAdd).length > 0
  const saveHints = (options: any) => {
    if (!hasChanges
    || !dbStatus || !dbStatus.snapshotId) { 
      return 
    }
    const payload : TableColumnRenameHintsDto = {
      databaseId: dbStatus?.databaseId,
      snapshotId: dbStatus?.snapshotId,
      hints: Object.values(states.effectiveHints).flatMap(x => x.hints),
    }
    editCtrls.save(payload, '', options)
  }

  return (
    <ESCRenameHintContext.Provider 
      value={{ 
        ...modifiers, 
        saveHints, 
        effectiveHints: states.effectiveHints,
        saving: editCtrls.saving,
        hasChanges,
        refetchHints: editCtrls.refetch,
      }}
    >
      {children}
    </ESCRenameHintContext.Provider>
  )
}

const useHintsModifier = (setStates: Dispatch<SetStateAction<State>>) => {
  return useMemo(() => ({
    addHint: (from: TableField, to: TableField) => {
      const baseKey = `${from.schemaName}!${to.schemaName}!${from.tableName}!${to.tableName}`
      const extKey = from.fieldName ? `${baseKey}!${from.fieldName}` : `${baseKey}*renameOnTable`
      
      setStates(v => {
        if (v.toRemove.has(extKey)) {
          v.toRemove.delete(extKey)
          return { ...v, effectiveHints: mergeToGroupedHints(v) }
  
        } else if (!v.existingHints[extKey]) {
          const hint: HintEntry = {
            schemaName: from.schemaName || '',
            newSchemaName: from.schemaName === to.schemaName ? undefined : to.schemaName,
            oldTableName: from.tableName || '',
            newTableName: to.tableName || '',
            oldFieldName: from.fieldName,
            newFieldName: to.fieldName,
          }
          v.toAdd[extKey] = hint
          return { ...v, effectiveHints: mergeToGroupedHints(v) }
        }
  
        return v
      })
    },
  
    removeHint: (hint: HintEntry) => {
      const baseKey = `${hint.schemaName}!${hint.newSchemaName || hint.schemaName}!${hint.oldTableName}!${hint.newTableName}`
      const renameOnTable = !hint.oldFieldName
      const extKey = renameOnTable ? `${baseKey}*renameOnTable` : `${baseKey}!${hint.oldFieldName}`
      
      setStates(v => {
        if (renameOnTable) {
          const keys = Object.keys(v.toAdd).filter(k => k === extKey || k.startsWith(baseKey + '!'))
          if (keys.length > 0) {
            keys.forEach(key => delete v.toAdd[key])
          }
          const shadeExisting = Object.keys(v.existingHints).some(k => k === extKey || k.startsWith(baseKey + '!'))
          if (shadeExisting) {
            v.toRemove.add(extKey)
          }
          if (keys.length > 0 || shadeExisting) {
            return { ...v, effectiveHints: mergeToGroupedHints(v) }
          }
  
        } else {
          if (v.toAdd[extKey]) {
            delete v.toAdd[extKey]
            return { ...v, effectiveHints: mergeToGroupedHints(v) }
    
          } else if (v.existingHints[extKey]) {
            v.toRemove.add(extKey)
            return { ...v, effectiveHints: mergeToGroupedHints(v) }
          }
        }
  
        return v
      })
    },

    resetEdits: () => {
      setStates(v => {
        const v1 = {...v, toAdd: {}, toRemove: new Set<string>() }
        v1.effectiveHints = mergeToGroupedHints(v1)
        return v1
      })
    }
    
  }) , [setStates])
}

const groupExistingHints = (renameHints: TableColumnRenameHintsDto|undefined) => {
  return (renameHints?.hints || []).reduce((acc, hint) => {
    const baseKey = `${hint.schemaName}!${hint.newSchemaName || hint.schemaName}!${hint.oldTableName}!${hint.newTableName}`
    const extKey = hint.oldFieldName ? `${baseKey}!${hint.oldFieldName}` : `${baseKey}*renameOnTable`
    acc[extKey] = hint
    return acc
  }, {} as Record<string, HintEntry>)
}

const mergeToGroupedHints = (
  states: { 
    existingHints: Record<string, HintEntry>, 
    toAdd: Record<string, HintEntry>, 
    toRemove: Set<string> 
  }, 
) => {
  const merged = {
    ...(states.toAdd),
    ...(states.existingHints),
  }
  const keys = Object.keys(merged)
  for(const extKey of states.toRemove.values()) {
    const renameOnTable = extKey.endsWith('*renameOnTable')
    if (renameOnTable) {
      const baseKey = extKey.substring(0, extKey.length - 14)
      keys.filter(k => k === extKey || k.startsWith(baseKey + '!'))
        .forEach(k => delete merged[k])
    } else {
      delete merged[extKey]
    }
  }

  return Object.keys(merged).reduce((acc, extKey) => {
    const i = extKey.lastIndexOf('!')
    const renamedOnTable = extKey.endsWith('*renameOnTable')
    const baseKey = renamedOnTable 
      ? extKey.substring(0, extKey.length - 14) 
      : extKey.substring(0, i)
    const bag = acc[baseKey] || 
      (acc[baseKey] = {
        renamedOnTable: false,
        hints: [],
      })
    bag.hints.push(merged[extKey])
    if (renamedOnTable) {
      bag.renamedOnTable = true
    }
    return acc
  }, {} as Record<string, { renamedOnTable:boolean, hints: HintEntry[] }>)   
}


export interface TableField {
  'schemaName'?: string | null;
  'tableName'?: string | null;
  'fieldName'?: string | null;
}
